import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Calendar, Check, MapPin, Truck, AlertTriangle, RotateCcw, Camera, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import ScanModal from "@/components/ScanModal";
import ModuleTabs from "@/components/ModuleTabs";

const SIZE_OPTIONS = ["20STD", "22GP", "20OT", "20RF", "40STD", "40HC", "40RF", "45GP"];
const STATUS_OPTIONS = ["EMPTY", "FULL"];
const CONDITION_OPTIONS = ["SOUND", "DAMAGED"];

function todayIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}
function fmt(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString(); }

export default function TruckingEntry() {
  const [date, setDate] = useState(todayIso());
  const [containerNo, setContainerNo] = useState("");
  const [containerNo2, setContainerNo2] = useState("");
  const [typeSize, setTypeSize] = useState("45GP");
  const [shippingLine, setShippingLine] = useState("Arkas");
  const [shippingLines, setShippingLines] = useState(["Arkas", "Hapag-Lloyd"]);
  const [fromLocation, setFromLocation] = useState("");
  const [status, setStatus] = useState("EMPTY");
  const [condition, setCondition] = useState("SOUND");
  const [shipmentRef, setShipmentRef] = useState("");
  const [truckPlate, setTruckPlate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [todayStats, setTodayStats] = useState({ count: 0, usd: 0 });
  const [rates, setRates] = useState({ rate_20ft_usd: 15, rate_40ft_usd: 30 });
  const [toast, setToast] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const inputRef = useRef(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };

  const fetchInitial = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/settings/trucking-rates"),
        api.get("/settings/shipping-lines"),
      ]);
      setRates(r.data);
      setShippingLines(s.data.values);
      if (s.data.values.length && !s.data.values.includes(shippingLine)) {
        setShippingLine(s.data.values[0]);
      }
    } catch (e) { console.warn("fetchInitial failed:", e?.message); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTodayStats = useCallback(async (forDate) => {
    try {
      const r = await api.get("/trucking", { params: { date_from: forDate, date_to: forDate, limit: 2000 } });
      const items = r.data.items;
      const usd = items.reduce((a, b) => a + (b.rate_usd || 0), 0);
      setTodayStats({ count: items.length, usd });
    } catch (e) { console.warn("fetchTodayStats failed:", e?.message); }
  }, []);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);
  useEffect(() => { fetchTodayStats(date); }, [date, fetchTodayStats]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const sizeIs20 = /^(20|22)/.test(typeSize);
  const baseRate = sizeIs20 ? rates.rate_20ft_usd : rates.rate_40ft_usd;
  const hasSecond = sizeIs20 && containerNo2.trim().length > 0;
  const computedRate = hasSecond ? baseRate * 2 : baseRate;

  // Clear second container if user switches away from a 20ft size
  useEffect(() => { if (!sizeIs20 && containerNo2) setContainerNo2(""); }, [sizeIs20, containerNo2]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = containerNo.trim().toUpperCase();
    const cleaned2 = containerNo2.trim().toUpperCase();
    if (!cleaned) { showToast("Container number is required", "err"); return; }
    if (!shippingLine) { showToast("Pick a shipping line", "err"); return; }
    if (cleaned2 && cleaned2 === cleaned) { showToast("Second container must differ from the first", "err"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/trucking", {
        date,
        container_no: cleaned,
        container_no_2: cleaned2,
        type_size: typeSize,
        shipping_line: shippingLine,
        from_location: fromLocation,
        status,
        condition,
        shipment_ref: shipmentRef,
        truck_plate: truckPlate,
      });
      const created = r.data;
      setRecent((prev) => [created, ...prev].slice(0, 10));
      setContainerNo("");
      setContainerNo2("");
      showToast(cleaned2 ? `Trucked ${cleaned} + ${cleaned2}` : `Trucked ${cleaned}`);
      fetchTodayStats(date);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to add", "err");
    } finally {
      setSubmitting(false);
    }
  };

  const undoLast = async (row) => {
    try {
      await api.delete(`/trucking/${row.id}`);
      setRecent((prev) => prev.filter((r) => r.id !== row.id));
      showToast(`Removed ${row.container_no}`);
      fetchTodayStats(date);
    } catch {
      showToast("Undo failed", "err");
    }
  };

  return (
    <div className="space-y-6 pb-24" data-testid="trucking-entry-page">
      <ModuleTabs module="trucking" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Truck size={12} /> Trucking</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
            Container trucked to port?
          </h1>
          <p className="mt-2 text-slate-600">Tap, type, done. Rate auto-applied by container size.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="px-2 py-1 rounded bg-slate-100 font-mono-tabular">20ft USD {rates.rate_20ft_usd}</span>
          <span className="px-2 py-1 rounded bg-slate-100 font-mono-tabular">40ft USD {rates.rate_40ft_usd}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="trucking-today-stats">
        <TallyCard label={date === todayIso() ? "Today" : date} value={fmt(todayStats.count)} sub="trips" accent="#0a2952" />
        <TallyCard label="USD billed" value={`USD ${fmt(todayStats.usd)}`} sub={`@ ${computedRate}/trip`} accent="#ff5b2c" />
        <TallyCard label="Next rate" value={`USD ${computedRate}`} sub={hasSecond ? "2 × 20ft chassis" : (sizeIs20 ? "20ft" : "40ft")} accent="#14b8a6" />
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 space-y-5" data-testid="trucking-entry-form">
        <div className="flex items-center gap-3">
          <Calendar size={16} className="text-slate-400" />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="trucking-date" className="form-input max-w-[200px]" />
          <button type="button" onClick={() => setDate(todayIso())} data-testid="trucking-today-btn"
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded transition-colors ${date === todayIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            Today
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="overline">Container number</label>
            <button type="button" onClick={() => setScanOpen(true)} data-testid="trucking-scan-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-[#0a2952] hover:bg-[#071f40] text-white px-3 py-1.5 rounded transition-colors">
              <Camera size={13} /> Scan
            </button>
          </div>
          <input ref={inputRef} type="text" value={containerNo}
            onChange={(e) => setContainerNo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
            placeholder="ARKU0000000" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={11}
            data-testid="trucking-container-no"
            className="w-full px-4 py-4 text-2xl sm:text-3xl font-mono-tabular font-bold tracking-wider text-[#0a2952] uppercase border-2 border-slate-300 rounded-md focus:border-[#ff5b2c] focus:ring-2 focus:ring-[#ff5b2c]/20 outline-none placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-widest" />
          <div className="mt-1.5 text-[11px] text-slate-400 font-mono-tabular">{containerNo.length}/11</div>
        </div>

        {sizeIs20 && (
          <div data-testid="trucking-container-2-section" className="-mt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="overline">2nd container · same chassis <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span></label>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#14b8a6]">2 × 20ft</span>
            </div>
            <input type="text" value={containerNo2}
              onChange={(e) => setContainerNo2(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="ARKU0000001" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={11}
              data-testid="trucking-container-no-2"
              className="w-full px-4 py-3 text-xl sm:text-2xl font-mono-tabular font-bold tracking-wider text-[#0a2952] uppercase border-2 border-slate-200 rounded-md focus:border-[#14b8a6] focus:ring-2 focus:ring-[#14b8a6]/20 outline-none placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-widest" />
            <div className="mt-1.5 text-[11px] text-slate-400 font-mono-tabular flex items-center justify-between">
              <span>{containerNo2.length}/11</span>
              {hasSecond && <span className="text-[#14b8a6] font-bold">Rate doubled · {baseRate} × 2 = USD {computedRate}</span>}
            </div>
          </div>
        )}

        <div>
          <label className="overline mb-2 block flex items-center gap-2">
            <Building2 size={12} /> Shipping line
          </label>
          <div className="flex flex-wrap gap-2" data-testid="trucking-shipping-line-buttons">
            {shippingLines.map((line) => (
              <button key={line} type="button" data-testid={`trucking-line-${line}`}
                onClick={() => setShippingLine(line)}
                className={`px-4 py-2.5 rounded-md font-semibold text-sm transition-all ${
                  shippingLine === line ? "bg-[#0a2952] text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}>
                {line}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline mb-2 block">Size</label>
          <div className="grid grid-cols-4 gap-2" data-testid="trucking-size-buttons">
            {SIZE_OPTIONS.map((t) => (
              <button key={t} type="button" data-testid={`trucking-size-${t}`} onClick={() => setTypeSize(t)}
                className={`px-2 py-2.5 rounded-md font-mono-tabular font-bold text-xs transition-all ${
                  typeSize === t ? (t.startsWith("20") || t.startsWith("22") ? "bg-[#14b8a6] text-white shadow scale-[1.02]" : "bg-[#0a2952] text-white shadow scale-[1.02]") : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline mb-2 block flex items-center gap-2">
            <MapPin size={12} /> From / Shipper / Consignee
          </label>
          <input type="text" value={fromLocation} onChange={(e) => setFromLocation(e.target.value.toUpperCase())}
            placeholder="SHED 6, MTG, THIRD PARTY, etc." data-testid="trucking-from"
            className="form-input font-mono-tabular" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline mb-2 block">Status</label>
            <div className="grid grid-cols-2 gap-2" data-testid="trucking-status-buttons">
              {STATUS_OPTIONS.map((s) => (
                <button key={s} type="button" data-testid={`trucking-status-${s}`} onClick={() => setStatus(s)}
                  className={`px-3 py-2.5 rounded-md font-bold text-xs uppercase tracking-wider transition-all ${
                    status === s ? "bg-[#0a2952] text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline mb-2 block">Condition</label>
            <div className="grid grid-cols-2 gap-2" data-testid="trucking-condition-buttons">
              {CONDITION_OPTIONS.map((c) => (
                <button key={c} type="button" data-testid={`trucking-condition-${c}`} onClick={() => setCondition(c)}
                  className={`px-3 py-2.5 rounded-md font-bold text-xs uppercase tracking-wider transition-all ${
                    condition === c ? (c === "SOUND" ? "bg-[#22c55e] text-white shadow" : "bg-red-600 text-white shadow") : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>{c}</button>
              ))}
            </div>
          </div>
        </div>

        <details className="border border-slate-200 rounded-md" data-testid="trucking-optional-fields">
          <summary className="cursor-pointer px-4 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#0a2952] select-none">
            Optional · Shipment ref &amp; truck plate
          </summary>
          <div className="px-4 pb-4 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Shipment ref</span>
              <input type="text" value={shipmentRef} onChange={(e) => setShipmentRef(e.target.value)}
                placeholder="e.g. 32701579" data-testid="trucking-shipment-ref"
                className="form-input font-mono-tabular" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Truck plate</span>
              <input type="text" value={truckPlate} onChange={(e) => setTruckPlate(e.target.value.toUpperCase())}
                placeholder="e.g. GT 8490 - 22" data-testid="trucking-truck-plate"
                className="form-input font-mono-tabular" />
            </label>
          </div>
        </details>

        <button type="submit" disabled={submitting || !containerNo.trim()} data-testid="trucking-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-40 disabled:cursor-not-allowed text-white py-4 rounded-md font-bold text-base shadow transition-all hover:-translate-y-[1px]">
          {submitting ? (<><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Adding...</>)
            : (<><Check size={18} /> {hasSecond ? `Add 2 × 20ft @ USD ${computedRate}` : `Add Trucking @ USD ${computedRate}`}</>)}
        </button>
      </form>

      {recent.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="trucking-recent">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="overline">Just added — this session</div>
              <div className="text-xs text-slate-500 mt-1">Tap undo to remove a mistake.</div>
            </div>
            <span className="text-xs font-bold text-[#ff5b2c] font-mono-tabular">{recent.length}</span>
          </div>
          <ul>
            {recent.map((row) => (
              <li key={row.id} data-testid={`trucking-recent-row-${row.id}`}
                className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <span className="font-mono-tabular text-base font-bold text-[#0a2952]">{row.container_no}</span>
                  {row.container_no_2 && (
                    <>
                      <span className="text-[10px] font-bold text-[#14b8a6]">+</span>
                      <span className="font-mono-tabular text-base font-bold text-[#0a2952]">{row.container_no_2}</span>
                    </>
                  )}
                  <span className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-bold tracking-wide bg-slate-100 text-[#0a2952] rounded-sm">{row.type_size}{row.container_no_2 ? " ×2" : ""}</span>
                  <span className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-bold tracking-wide bg-[#0a2952]/10 text-[#0a2952] rounded-sm">{row.shipping_line}</span>
                  {row.from_location && <span className="hidden sm:inline-block text-[10px] text-slate-500">{row.from_location}</span>}
                  {row.truck_plate && <span className="hidden md:inline-block text-[10px] text-slate-400 font-mono-tabular">{row.truck_plate}</span>}
                  <span className="text-xs text-[#ff5b2c] font-mono-tabular font-bold">USD {row.rate_usd}</span>
                </div>
                <button type="button" data-testid={`trucking-undo-btn-${row.id}`} onClick={() => undoLast(row)}
                  className="ml-3 w-8 h-8 grid place-items-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <RotateCcw size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {recent.length === 0 && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md px-5 py-8 text-center text-sm text-slate-500">
          No trucking trips added yet in this session.
          <div className="mt-2">
            <Link to="/trucking/records" className="text-xs text-[#ff5b2c] font-semibold hover:underline">
              Browse all trucking records →
            </Link>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md shadow-lg text-sm font-semibold flex items-center gap-2 ${
            toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
          }`}>
          {toast.kind === "err" ? <AlertTriangle size={16} /> : <Check size={16} />}
          {toast.msg}
        </div>
      )}

      <ScanModal open={scanOpen} onClose={() => setScanOpen(false)}
        onResult={(num) => {
          const dup = recent.find(
            (r) => r.date === date && (r.container_no === num || r.container_no_2 === num),
          );
          if (dup) {
            showToast(`${num} already trucked today — skipped`, "err");
            return;
          }
          setContainerNo(num); showToast(`Scanned ${num}`); setTimeout(() => inputRef.current?.focus(), 50);
        }} />
    </div>
  );
}

function TallyCard({ label, value, sub, accent }) {
  return (
    <div className="relative bg-white border border-slate-200 rounded-md p-3 sm:p-4 overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">{label}</div>
      <div className="font-display font-extrabold text-lg sm:text-2xl text-[#0a2952] mt-1 leading-none font-mono-tabular truncate">{value}</div>
      <div className="text-[10px] text-slate-400 mt-1 font-mono-tabular truncate">{sub}</div>
    </div>
  );
}
